import {
  capitalizeFirstLetter,
  getFirstStartedAtTs,
  getLastEndedAtTs,
} from "@intelligems/ig-utils";

import {
  AnalyticsActiveFilterType,
  IExperienceDashboardFilters,
} from "../../store/types/dashboardFilters";
import { ISitewideDashboardFilters } from "../../store/types/sitewideDashboardFilters";
import { ExperienceFe } from "../../types/schemas/experiences";
import { fullDateTimeConverter } from "../../utils/dateConverters";

export type AnalyticsFilterReducerAction =
  | {
      type: "updateTestOrdersOnly";
      payload: { dashboardFilters: IExperienceDashboardFilters };
    }
  | {
      type: "updateIsZScoreEnabled";
      payload: { dashboardFilters: IExperienceDashboardFilters };
    }
  | {
      type: "updateDiscountIncludeType";
      payload: { dashboardFilters: IExperienceDashboardFilters };
    }
  | {
      type: "updateDeviceType";
      payload: { dashboardFilters: IExperienceDashboardFilters };
    }
  | {
      type: "updateSitewideDeviceType";
      payload: { sitewideDashboardFilters: ISitewideDashboardFilters };
    }
  | {
      type: "updateSitewideVisitorType";
      payload: { sitewideDashboardFilters: ISitewideDashboardFilters };
    }
  | {
      type: "updateSitewideUrlParam";
      payload: { sitewideDashboardFilters: ISitewideDashboardFilters };
    }
  | {
      type: "updateSitewideChannelType";
      payload: { sitewideDashboardFilters: ISitewideDashboardFilters };
    }
  | {
      type: "updateSitewideCountryCodes";
      payload: { sitewideDashboardFilters: ISitewideDashboardFilters };
    }
  | {
      type: "updateSitewideSubscriptionType";
      payload: { sitewideDashboardFilters: ISitewideDashboardFilters };
    }
  | {
      type: "updateCustomEvents";
      payload: { dashboardFilters: IExperienceDashboardFilters };
    }
  | {
      type: "updateCountryCodes";
      payload: { dashboardFilters: IExperienceDashboardFilters };
    }
  | {
      type: "updateVisitorType";
      payload: { dashboardFilters: IExperienceDashboardFilters };
    }
  | {
      type: "updateUrlParam";
      payload: { dashboardFilters: IExperienceDashboardFilters };
    }
  | {
      type: "updateUserBehavior";
      payload: { dashboardFilters: IExperienceDashboardFilters };
    }
  | {
      type: "updateChannelType";
      payload: { dashboardFilters: IExperienceDashboardFilters };
    }
  | {
      type: "updateEndExperience";
      payload: {
        dashboardFilters: IExperienceDashboardFilters;
        experience?: ExperienceFe;
        timezone: string;
      };
    }
  | {
      type: "updateEndCampaign";
      payload: {
        dashboardFilters: IExperienceDashboardFilters;
        experience: ExperienceFe;
        timezone: string;
      };
    }
  | {
      type: "updateStartExperience";
      payload: {
        dashboardFilters: IExperienceDashboardFilters;
        experience: ExperienceFe;
        timezone: string;
      };
    }
  | {
      type: "updateStartCampaign";
      payload: {
        dashboardFilters: IExperienceDashboardFilters;
        experience: ExperienceFe;
        timezone: string;
      };
    };

export function AnalyticsFilterReducer(
  draft: AnalyticsActiveFilterType[],
  action: AnalyticsFilterReducerAction
) {
  switch (action.type) {
    case "updateTestOrdersOnly":
      if (
        action.payload.dashboardFilters.testOrdersOnly === true &&
        !draft.some((chip) => chip.key === "testOrdersOnly")
      ) {
        draft = [
          ...draft,
          {
            key: "testOrdersOnly",
            value: true,
            label: "Test Products Only",
          },
        ];
      } else if (
        action.payload.dashboardFilters.testOrdersOnly === false &&
        draft.some((chip) => chip.key === "testOrdersOnly")
      ) {
        draft = draft.filter((chip) => {
          return chip.key !== "testOrdersOnly";
        });
      }
      return draft;

    case "updateIsZScoreEnabled":
      if (action.payload.dashboardFilters.isZScoreEnabled === true) {
        draft = [
          ...draft.filter((chip) => {
            return chip.key !== "isZScoreEnabled";
          }),
          {
            key: "isZScoreEnabled",
            value: true,
            label: `Max Z-Score: ${action.payload.dashboardFilters.maxZScore}`,
          },
        ];
      } else if (
        action.payload.dashboardFilters.isZScoreEnabled === false &&
        draft.some((chip) => chip.key === "isZScoreEnabled")
      ) {
        draft = draft.filter((chip) => {
          return chip.key !== "isZScoreEnabled";
        });
      }
      return draft;
    case "updateDiscountIncludeType":
      if (
        action.payload.dashboardFilters.discountIncludeType === 2 &&
        !draft.some((chip) => chip.key === "discountIncludeType")
      ) {
        draft = [
          ...draft,
          {
            key: "discountIncludeType",
            value: 2,
            label: "IG Offer Only",
          },
        ];
      } else if (
        action.payload.dashboardFilters.discountIncludeType === 1 &&
        draft.some((chip) => chip.key === "discountIncludeType")
      ) {
        draft = draft.filter((chip) => {
          return chip.key !== "discountIncludeType";
        });
      }
      return draft;

    case "updateDeviceType":
      if (
        action.payload.dashboardFilters.deviceType === null &&
        draft.some((chip) => chip.key === "deviceType")
      ) {
        draft = draft.filter((chip) => {
          return chip.key !== "deviceType";
        });
      } else if (
        action.payload.dashboardFilters.deviceType === "desktop" ||
        action.payload.dashboardFilters.deviceType === "mobile"
      ) {
        draft = [
          ...draft.filter((chip) => {
            return chip.key !== "deviceType";
          }),
          {
            key: "deviceType",
            value: action.payload.dashboardFilters.deviceType,
            label: `${capitalizeFirstLetter(
              action.payload.dashboardFilters.deviceType
            )}`,
          },
        ];
      }
      return draft;
    case "updateSitewideDeviceType":
      if (
        action.payload.sitewideDashboardFilters.deviceType === "any" &&
        draft.some((chip) => chip.key === "deviceType")
      ) {
        draft = draft.filter((chip) => {
          return chip.key !== "deviceType";
        });
      } else if (
        (action.payload.sitewideDashboardFilters.deviceType === "desktop" ||
          action.payload.sitewideDashboardFilters.deviceType === "mobile") &&
        !draft.some((chip) => chip.key === "deviceType")
      ) {
        draft = [
          ...draft,
          {
            key: "deviceType",
            value: action.payload.sitewideDashboardFilters.deviceType,
            label: `${capitalizeFirstLetter(
              action.payload.sitewideDashboardFilters.deviceType
            )}`,
          },
        ];
      }
      return draft;
    case "updateCustomEvents":
      if (
        action.payload.dashboardFilters.customEvents?.length === 0 &&
        draft.some((chip) => chip.key === "customEvents")
      ) {
        draft = draft.filter((chip) => {
          return chip.key !== "customEvents";
        });
      } else if (
        action.payload.dashboardFilters.customEvents?.length !== 0 &&
        !draft.some((chip) => chip.key === "customEvents")
      ) {
        let customEventsLabel: string | undefined = "";
        if (
          action.payload.dashboardFilters.customEvents &&
          action.payload.dashboardFilters.customEvents.length > 2
        ) {
          customEventsLabel = action.payload.dashboardFilters.customEvents
            .slice(0, 2)
            .join(", ");
          customEventsLabel =
            customEventsLabel +
            `, ... + ${
              action.payload.dashboardFilters.customEvents!.length - 2
            } more`;
        } else {
          customEventsLabel =
            action.payload.dashboardFilters.customEvents?.join(", ");
        }
        draft = [
          ...draft,
          {
            key: "customEvents",
            value: action.payload.dashboardFilters.customEvents,
            label: `Custom Events: ${customEventsLabel}`,
          },
        ];
      }
      return draft;
    case "updateCountryCodes":
      if (
        action.payload.dashboardFilters.countryCodes?.length === 0 &&
        draft.some((chip) => chip.key === "countryCodes")
      ) {
        draft = draft.filter((chip) => {
          return chip.key !== "countryCodes";
        });
      } else if (action.payload.dashboardFilters.countryCodes?.length !== 0) {
        draft = [
          ...draft.filter((chip) => {
            return chip.key !== "countryCodes";
          }),
          {
            key: "countryCodes",
            value: action.payload.dashboardFilters.countryCodes,
            label: `Country Codes: ${action.payload.dashboardFilters.countryCodes?.join(
              ", "
            )}`,
          },
        ];
      }
      return draft;
    case "updateSitewideCountryCodes":
      if (
        action.payload.sitewideDashboardFilters.countryCodes?.length === 0 &&
        draft.some((chip) => chip.key === "countryCodes")
      ) {
        draft = draft.filter((chip) => {
          return chip.key !== "countryCodes";
        });
      } else if (
        action.payload.sitewideDashboardFilters.countryCodes?.length !== 0 &&
        !draft.some((chip) => chip.key === "countryCodes")
      ) {
        draft = [
          ...draft,
          {
            key: "countryCodes",
            value: action.payload.sitewideDashboardFilters.countryCodes,
            label: `Country Codes: ${action.payload.sitewideDashboardFilters.countryCodes?.join(
              ", "
            )}`,
          },
        ];
      }
      return draft;
    case "updateVisitorType":
      if (
        !action.payload.dashboardFilters.visitorType?.length &&
        draft.some((chip) => chip.key === "visitorType")
      ) {
        draft = draft.filter((chip) => {
          return chip.key !== "visitorType";
        });
      } else if (
        action.payload.dashboardFilters.visitorType === "new" ||
        action.payload.dashboardFilters.visitorType === "returning"
      ) {
        draft = [
          ...draft.filter((chip) => {
            return chip.key !== "visitorType";
          }),
          {
            key: "visitorType",
            value: action.payload.dashboardFilters.visitorType,
            label: `${capitalizeFirstLetter(
              action.payload.dashboardFilters.visitorType
            )}`,
          },
        ];
      }
      return draft;

    case "updateUrlParam":
      if (
        !action.payload.dashboardFilters.urlParam?.queryParam &&
        draft.some((chip) => chip.key === "urlParam")
      ) {
        draft = draft.filter((chip) => {
          return chip.key !== "urlParam";
        });
      } else if (action.payload.dashboardFilters.urlParam?.queryParam) {
        draft = [
          ...draft.filter((chip) => {
            return chip.key !== "urlParam";
          }),
          {
            key: "urlParam",
            value: action.payload.dashboardFilters.urlParam,
            label: "Url Param Enabled",
          },
        ];
      }

      return draft;

    case "updateSitewideVisitorType":
      if (
        !action.payload.sitewideDashboardFilters.visitorType &&
        draft.some((chip) => chip.key === "visitorType")
      ) {
        draft = draft.filter((chip) => {
          return chip.key !== "visitorType";
        });
      } else if (
        (action.payload.sitewideDashboardFilters.visitorType === "new" ||
          action.payload.sitewideDashboardFilters.visitorType ===
            "returning") &&
        !draft.some((chip) => chip.key === "visitorType")
      ) {
        draft = [
          ...draft,
          {
            key: "visitorType",
            value: action.payload.sitewideDashboardFilters.visitorType,
            label: `${capitalizeFirstLetter(
              action.payload.sitewideDashboardFilters.visitorType
            )}`,
          },
        ];
      }
      return draft;
    case "updateSitewideSubscriptionType":
      if (
        action.payload.sitewideDashboardFilters.subscriptionType === "any" &&
        draft.some((chip) => chip.key === "subscriptionType")
      ) {
        draft = draft.filter((chip) => {
          return chip.key !== "subscriptionType";
        });
      } else if (
        (action.payload.sitewideDashboardFilters.subscriptionType ===
          "One Time" ||
          action.payload.sitewideDashboardFilters.subscriptionType ===
            "Recurring") &&
        !draft.some((chip) => chip.key === "subscriptionType")
      ) {
        draft = [
          ...draft,
          {
            key: "subscriptionType",
            value: action.payload.sitewideDashboardFilters.subscriptionType,
            label: `Subscription Type: ${action.payload.sitewideDashboardFilters.subscriptionType}`,
          },
        ];
      }
      return draft;
    case "updateUserBehavior":
      if (
        !action.payload.dashboardFilters.userBehavior?.addedToCart &&
        !action.payload.dashboardFilters.userBehavior?.viewedCheckout &&
        draft.some((chip) => chip.key === "userBehavior")
      ) {
        draft = draft.filter((chip) => {
          return chip.key !== "userBehavior";
        });
      } else if (
        action.payload.dashboardFilters.userBehavior?.addedToCart ||
        action.payload.dashboardFilters.userBehavior?.viewedCheckout
        // &&
        // !draft.some((chip) => chip.key === "userBehavior")
      ) {
        draft = [
          ...draft.filter((chip) => {
            return chip.key !== "userBehavior";
          }),
          {
            key: "userBehavior",
            value: action.payload.dashboardFilters.userBehavior,
            label: "User Behavior Enabled",
          },
        ];
      }

      return draft;

    case "updateSitewideUrlParam":
      if (
        !action.payload.sitewideDashboardFilters.urlParam?.queryParam &&
        draft.some((chip) => chip.key === "urlParam")
      ) {
        draft = draft.filter((chip) => {
          return chip.key !== "urlParam";
        });
      } else if (action.payload.sitewideDashboardFilters.urlParam?.queryParam) {
        draft = [
          ...draft.filter((chip) => {
            return chip.key !== "urlParam";
          }),
          {
            key: "urlParam",
            value: action.payload.sitewideDashboardFilters.urlParam,
            label: "Url Param Enabled",
          },
        ];
      }

      return draft;

    case "updateChannelType":
      if (
        !action.payload.dashboardFilters.channels?.length &&
        draft.some((chip) => chip.key === "channels")
      ) {
        draft = draft.filter((chip) => {
          return chip.key !== "channels";
        });
      } else if (
        (action.payload.dashboardFilters.channels ?? []).length > 0 &&
        action.payload.dashboardFilters.channels
      ) {
        draft = [
          ...draft.filter((chip) => {
            return chip.key !== "channels";
          }),
          {
            key: "channels",
            value: action.payload.dashboardFilters.channels,
            label: `${action.payload.dashboardFilters.channels?.join(", ")}`,
          },
        ];
      }
      return draft;
    case "updateSitewideChannelType":
      if (
        !action.payload.sitewideDashboardFilters.channels &&
        draft.some((chip) => chip.key === "channels")
      ) {
        draft = draft.filter((chip) => {
          return chip.key !== "channels";
        });
      } else if (
        (action.payload.sitewideDashboardFilters.channels ?? []).length > 0 &&
        action.payload.sitewideDashboardFilters.channels
      ) {
        draft = [
          ...draft.filter((chip) => {
            return chip.key !== "channels";
          }),
          {
            key: "channels",
            value: action.payload.sitewideDashboardFilters.channels,
            label: `${action.payload.sitewideDashboardFilters.channels?.join(
              ", "
            )}`,
          },
        ];
      }
      return draft;
    case "updateEndExperience":
      if (action.payload.dashboardFilters.endTime) {
        const initialEndVal: AnalyticsActiveFilterType = {
          key: "endTime",
          value: action.payload.dashboardFilters.endTime!,
          label: `End Time: ${fullDateTimeConverter(
            action.payload.dashboardFilters.endTime,
            action.payload.timezone,
            undefined,
            true
          )}`,
        };
        // if experience is ended (endedAts)
        if (action.payload.experience) {
          const latestEndedAtTs = getLastEndedAtTs(
            action.payload.experience.experienceActions
          );

          // if filter already set
          if (draft.some((chip) => chip.key === "endTime")) {
            const currFilterVal = draft.findIndex(
              (chip) => chip.key === "endTime"
            );
            if (draft[currFilterVal].value !== latestEndedAtTs) {
              draft = draft.map((chip, index) => {
                return index === currFilterVal ? initialEndVal : chip;
              });
            } else if (
              // if filter was cleared
              latestEndedAtTs === action.payload.dashboardFilters.endTime
            ) {
              draft = draft.filter((chip) => {
                return chip.key !== "endTime";
              });
            }
          } else {
            if (action.payload.dashboardFilters.endTime !== latestEndedAtTs) {
              draft = [...draft, initialEndVal];
            }
          }
        } else {
          if (draft.some((chip) => chip.key === "endTime")) {
            const currFilterVal = draft.findIndex(
              (chip) => chip.key === "endTime"
            );
            // update filter
            if (draft[currFilterVal].value !== null) {
              draft = draft.map((chip, index) => {
                return index === currFilterVal ? initialEndVal : chip;
              });
            } else {
              // remove filter
              draft = draft.filter((chip) => {
                return chip.key !== "endTime";
              });
            }
          } else {
            if (action.payload.dashboardFilters.endTime) {
              draft = [...draft, initialEndVal];
            }
          }
        }
      } else if (
        !action.payload.dashboardFilters.endTime &&
        draft.some((chip) => chip.key === "endTime")
      ) {
        draft = draft.filter((chip) => {
          return chip.key !== "endTime";
        });
      }

      return draft;

    case "updateEndCampaign":
      if (action.payload.dashboardFilters.endTime) {
        const initialEndVal: AnalyticsActiveFilterType = {
          key: "endTime",
          value: action.payload.dashboardFilters.endTime!,
          label: `End Time: ${fullDateTimeConverter(
            action.payload.dashboardFilters.endTime,
            action.payload.timezone,
            undefined,
            true
          )}`,
        };
        // if experience is ended (endedAts)
        if (action.payload.experience) {
          const latestEndedAtTs = getLastEndedAtTs(
            action.payload.experience.experienceActions
          );
          // if filter already set
          if (draft.some((chip) => chip.key === "endTime")) {
            const currFilterVal = draft.findIndex(
              (chip) => chip.key === "endTime"
            );
            if (draft[currFilterVal].value !== latestEndedAtTs) {
              draft = draft.map((chip, index) => {
                return index === currFilterVal ? initialEndVal : chip;
              });
            } else if (
              // if filter was cleared
              latestEndedAtTs === action.payload.dashboardFilters.endTime
            ) {
              draft = draft.filter((chip) => {
                return chip.key !== "endTime";
              });
            }
          } else {
            if (action.payload.dashboardFilters.endTime !== latestEndedAtTs) {
              draft = [...draft, initialEndVal];
            }
          }
        } else {
          if (draft.some((chip) => chip.key === "endTime")) {
            const currFilterVal = draft.findIndex(
              (chip) => chip.key === "endTime"
            );
            // update filter
            if (draft[currFilterVal].value !== null) {
              draft = draft.map((chip, index) => {
                return index === currFilterVal ? initialEndVal : chip;
              });
            } else {
              // remove filter
              draft = draft.filter((chip) => {
                return chip.key !== "endTime";
              });
            }
          } else {
            if (action.payload.dashboardFilters.endTime) {
              draft = [...draft, initialEndVal];
            }
          }
        }
      } else if (
        !action.payload.dashboardFilters.endTime &&
        draft.some((chip) => chip.key === "endTime")
      ) {
        draft = draft.filter((chip) => {
          return chip.key !== "endTime";
        });
      }

      return draft;

    case "updateStartExperience":
      if (action.payload.dashboardFilters.startTime) {
        const dashboardFilterVal: AnalyticsActiveFilterType = {
          key: "startTime",
          value: action.payload.dashboardFilters.startTime!,
          label: `Start Time: ${fullDateTimeConverter(
            action.payload.dashboardFilters.startTime,
            action.payload.timezone,
            undefined,
            true
          )}`,
        };

        if (action.payload.experience) {
          const initialStartedAtTs = getFirstStartedAtTs(
            action.payload.experience.experienceActions
          );
          // if filter already set
          if (draft.some((chip) => chip.key === "startTime")) {
            const currFilterVal = draft.findIndex(
              (chip) => chip.key === "startTime"
            );
            // if start filter is updated
            if (
              initialStartedAtTs === action.payload.dashboardFilters.startTime
            ) {
              draft = draft.filter((chip) => {
                return chip.key !== "startTime";
              });
            } else if (
              dashboardFilterVal.value !== draft[currFilterVal]!.value
            ) {
              draft = draft.map((chip, index) => {
                return index === currFilterVal ? dashboardFilterVal : chip;
              });
            }
          }
          // initial filter set
          else if (
            initialStartedAtTs !== action.payload.dashboardFilters.startTime &&
            !draft.some((chip) => chip.key === "startTime")
          ) {
            draft = [...draft, dashboardFilterVal];
          }
        }
      } else if (
        !action.payload.dashboardFilters.startTime &&
        draft.some((chip) => chip.key === "startTime")
      ) {
        draft = draft.filter((chip) => {
          return chip.key !== "startTime";
        });
      }

      return draft;

    case "updateStartCampaign":
      if (action.payload.dashboardFilters.startTime) {
        const dashboardFilterVal: AnalyticsActiveFilterType = {
          key: "startTime",
          value: action.payload.dashboardFilters.startTime!,
          label: `Start Time: ${fullDateTimeConverter(
            action.payload.dashboardFilters.startTime,
            action.payload.timezone,
            undefined,
            true
          )}`,
        };
        const initialStartedAtTs = getFirstStartedAtTs(
          action.payload.experience.experienceActions
        );

        // if filter already set
        if (draft.some((chip) => chip.key === "startTime")) {
          const currFilterVal = draft.findIndex(
            (chip) => chip.key === "startTime"
          );
          // if start filter is updated
          if (
            initialStartedAtTs === action.payload.dashboardFilters.startTime
          ) {
            draft = draft.filter((chip) => {
              return chip.key !== "startTime";
            });
          } else if (dashboardFilterVal.value !== draft[currFilterVal]!.value) {
            draft = draft.map((chip, index) => {
              return index === currFilterVal ? dashboardFilterVal : chip;
            });
          }
        }
        // initial filter set
        else if (
          initialStartedAtTs !== action.payload.dashboardFilters.startTime &&
          !draft.some((chip) => chip.key === "startTime")
        ) {
          draft = [...draft, dashboardFilterVal];
        }
      }
      return draft;
  }
}
